import { Phone, Check, Loader2 } from "lucide-react";
import { PatientDetailsCard } from "./PatientDetailsCard";
import {
  PatientDetails,
  PatientFamilyDetails,
} from "./types";
import doctorIllustration from "../../assets/images/appointment-section.svg";

interface StepPatientProps {
  mobileNumber: string;
  onMobileNumberChange: (value: string) => void;
  isVerified: boolean;
  isVerifying: boolean;
  onVerify: () => void;
  patientDetails: PatientDetails | null;
  setPatientDetails: (p: any) => void;
  previouslyConsultedDoctors: any[];
  previousFamilyDetails: PatientFamilyDetails[];
  selectedFamilyMember: any;
  setSelectedFamilyMember: (f: any) => void;
  onAddFamilyMember: () => void;
}

export function StepPatient({
  mobileNumber,
  onMobileNumberChange,
  isVerified,
  isVerifying,
  onVerify,
  patientDetails,
  setPatientDetails,
  previouslyConsultedDoctors,
  previousFamilyDetails,
  selectedFamilyMember,
  setSelectedFamilyMember,
  onAddFamilyMember,
}: StepPatientProps) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-full">
      {/* Left Side — Form */}
      <div className="flex flex-col">
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-5">
            Patient Details
          </h3>

          {/* Mobile Number Input */}
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-gray-700">
              Mobile Number <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Phone className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="tel"
                value={mobileNumber.trim()}
                onChange={(e) => onMobileNumberChange(e.target.value.trim())}
                placeholder="Enter Mobile Number"
                className={`block w-full pl-10 pr-24 py-2.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-colors ${isVerified ? "bg-gray-50" : ""
                  }`}
                disabled={isVerified || isVerifying}
                pattern="\d{10}"
                maxLength={10}
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                {isVerified ? (
                  <div className="flex items-center gap-1.5 pr-1">
                    <Check className="h-5 w-5 text-emerald-600" />
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={onVerify}
                    disabled={!mobileNumber || isVerifying}
                    className={`px-4 py-1.5 text-sm font-semibold rounded-md transition-colors ${isVerifying
                        ? "text-gray-400 cursor-not-allowed"
                        : "text-emerald-600 hover:bg-emerald-50"
                      }`}
                  >
                    {isVerifying ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      "Verify"
                    )}
                  </button>
                )}
              </div>
            </div>
            {isVerified && (
              <p className="flex items-center gap-1.5 text-sm text-emerald-600 mt-1">
                <Check className="w-4 h-4" />
                Patient record found
              </p>
            )}
          </div>

          {/* Patient Details Card */}
          <PatientDetailsCard
            isVerified={isVerified}
            patientDetails={
              patientDetails
                ? { ...patientDetails, mobileNumber }
                : null
            }
            setPatientDetails={setPatientDetails}
            selectedDoctor={null}
            setSelectedDoctor={() => { }}
            previouslyConsultedDoctors={previouslyConsultedDoctors}
            previousFamilyDetails={previousFamilyDetails}
            selectedFamilyMember={selectedFamilyMember}
            setSelectedFamilyMember={setSelectedFamilyMember}
          />

          {/* Add Family Member */}
          {isVerified && (
            <button
              onClick={onAddFamilyMember}
              className="mt-4 w-full py-2.5 border-2 border-dashed border-emerald-300 text-emerald-600 rounded-lg hover:bg-emerald-50 hover:border-emerald-400 transition-colors text-sm font-medium"
            >
              + Add Family Member
            </button>
          )}
        </div>

        <p className="text-sm text-gray-500 mt-4 text-center">
          Start by confirming who this appointment is for.
        </p>
      </div>

      {/* Right Side — Illustration */}
      <div className="hidden lg:flex flex-col items-center justify-center">
        <img
          src={doctorIllustration}
          alt="Doctor Illustration"
          className="w-auto h-80 opacity-80"
        />
      </div>
    </div>
  );
}
